import type {
  ExpressionSpecification,
  FilterSpecification,
  LayerSpecification,
  MapOptions,
  StyleSpecification,
  SymbolLayerSpecification,
} from "maplibre-gl";
import type { ThemeData } from "@/cms/models";
import type { LookScheme, ThemeWithLookSystem } from "@/types/look-system";
import {
  contrast,
  hexLuminance,
  isColour,
  record,
} from "@/utils/colour-values";
import { ownColourInDark, paletteSchemes } from "@/utils/colour-versions";
import { isDerivedV2, isLookSystem } from "@/utils/look-gate";
import { MAP_GLYPHS_URL } from "@/utils/map-glyphs";
import { ROLE_PALETTE_KEYS, resolvePaletteKey } from "@/utils/palette-roles";

export type CaseMapTheme =
  | "site"
  | "light"
  | "grey"
  | "dark"
  | "sand"
  | "nordic";
export type CaseMapZoom = "near" | "medium" | "far";
export type MapTilesRegion = "dk" | "gl";

/** The colours of the map's layers, and `ink`, the pin's colour where the brand colour does not stand out from `land`. */
export interface CaseMapPalette {
  land: string;
  landuse: string;
  water: string;
  buildings: string;
  roads: string;
  ink: string;
}

/** Sagskort Deluxe's `specifics` as stored; every key is optional. */
export interface CaseMapDeluxeSpecifics {
  /** Absent: »Følger hjemmesidens farver«. */
  mapTheme?: CaseMapTheme;
  /** A CSS colour; absent: the website's brand colour. */
  pinColor?: string;
  /** Absent: true. */
  zoomControls?: boolean;
  /** 16, 14 or 12; absent: medium. */
  zoom?: CaseMapZoom;
  /** Zoomniveau, a whole level 8–18 that wins over `zoom`. */
  zoomLevel?: number;
  /** Vis navne: place, street, water and landmark names; absent: false. */
  showLabels?: boolean;
  /** Zoom ind ved visning; absent: false. */
  zoomIntro?: boolean;
  /** Start fra zoomniveau, a whole level 3–17, kept below the map's zoom; absent: four levels out, at least 3. */
  zoomIntroFrom?: number;
  /** Varighed in whole milliseconds, 200–5000; absent: 500. */
  zoomIntroDuration?: number;
}

/** Zoom ind ved visning: the map opens at `from` and glides in to its zoom over `duration` milliseconds. */
export interface CaseMapIntro {
  from: number;
  duration: number;
}

export interface CaseMapDeluxeSettings {
  theme: CaseMapTheme;
  /** The stored Nålens farve, a CSS colour; undefined uses the website's brand colour. */
  pinColor?: string;
  zoomControls: boolean;
  /** The zoom the map shows the case at, after the intro where there is one. */
  zoom: number;
  showLabels: boolean;
  /** Only with Zoom ind ved visning. */
  intro?: CaseMapIntro;
}

/** One colour scheme's map: its palette and its pin. */
export interface CaseMapScheme {
  palette: CaseMapPalette;
  /** The stored Nålens farve, drawn as the page resolves it and never replaced; absent where none is stored. */
  pin?: string;
  /** The website's brand colour: the pin where none is stored or the stored one resolves to no colour. */
  brand: string;
}

export interface CaseMapDeluxeView
  extends Omit<CaseMapDeluxeSettings, "pinColor"> {
  light: CaseMapScheme;
  dark: CaseMapScheme;
}

export const CASE_MAP_ZOOM: Record<CaseMapZoom, number> = {
  near: 16,
  medium: 14,
  far: 12,
};

/** Fixed, tuned palettes: every layer stays calm on `land`, green areas read as green and water as blue (NATURAL_HUES),
 *  and `ink` reads on the land at 4.5:1. */
export const CASE_MAP_THEMES: Record<
  Exclude<CaseMapTheme, "site">,
  CaseMapPalette
> = {
  light: {
    land: "#f4f2ee",
    landuse: "#dcefd5",
    water: "#badaec",
    buildings: "#e1dcd4",
    roads: "#ffffff",
    ink: "#1f2328",
  },
  grey: {
    land: "#e9eaeb",
    landuse: "#d5e4d5",
    water: "#c1d4e2",
    buildings: "#d6d8da",
    roads: "#ffffff",
    ink: "#26292c",
  },
  dark: {
    land: "#1f2327",
    landuse: "#1f2d22",
    water: "#0a1824",
    buildings: "#2d3338",
    roads: "#3d454c",
    ink: "#f1f3f5",
  },
  sand: {
    land: "#f2eadb",
    landuse: "#d9e4c8",
    water: "#bbd7e4",
    buildings: "#e2d4bd",
    roads: "#fbf8f1",
    ink: "#3a3128",
  },
  nordic: {
    land: "#edf1f2",
    landuse: "#d4ebdb",
    water: "#b1cfe2",
    buildings: "#d9e0e4",
    roads: "#ffffff",
    ink: "#1d2b36",
  },
};

/**
 * The pin: the map-marker teardrop in a 24-unit icon, its tip at (12, 22), with the round hole at (12, 9) cut out by the
 * evenodd fill rule, so the map shows through it.
 */
export const CASE_MAP_PIN_PATH =
  "M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zM12 6.5a2.5 2.5 0 1 0 0 5a2.5 2.5 0 1 0 0-5z";

/** The brand colour must stand out from the land by at least this much to be the pin without a stored colour (WCAG
 *  non-text contrast). A stored colour is drawn whatever its contrast. */
export const PIN_CONTRAST = 3;

const THEMES = new Set<string>(["site", ...Object.keys(CASE_MAP_THEMES)]);
/** A value the page resolves as a colour, with nothing that could end a declaration: a hex code, a colour name, a colour
 *  function or a custom property, also with a fallback. */
const CSS_COLOUR =
  /^(?:#[0-9a-f]{3,8}|[a-z]{3,20}|(?:rgba?|hsla?|hwb|lab|lch|oklab|oklch|color|color-mix|var)\([\w\s#.,%/()+-]{1,200}\))$/i;

const wholeIn = (value: unknown, min: number, max: number) =>
  typeof value === "number" &&
  Number.isInteger(value) &&
  value >= min &&
  value <= max
    ? value
    : undefined;

const INTRO_DURATION = 500;

function caseMapIntro(
  zoom: number,
  from: unknown,
  duration: unknown,
): CaseMapIntro {
  return {
    from: Math.min(zoom - 1, wholeIn(from, 3, 17) ?? Math.max(3, zoom - 4)),
    duration: wholeIn(duration, 200, 5000) ?? INTRO_DURATION,
  };
}

export function caseMapDeluxeSettings(
  specifics: unknown,
): CaseMapDeluxeSettings {
  const {
    mapTheme,
    pinColor,
    zoomControls,
    zoom,
    zoomLevel,
    showLabels,
    zoomIntro,
    zoomIntroFrom,
    zoomIntroDuration,
  } = record(specifics);
  const pin = typeof pinColor === "string" ? pinColor.trim() : "";
  const target =
    wholeIn(zoomLevel, 8, 18) ??
    (typeof zoom === "string" && Object.hasOwn(CASE_MAP_ZOOM, zoom)
      ? CASE_MAP_ZOOM[zoom as CaseMapZoom]
      : CASE_MAP_ZOOM.medium);
  return {
    theme:
      typeof mapTheme === "string" && THEMES.has(mapTheme)
        ? (mapTheme as CaseMapTheme)
        : "site",
    pinColor: CSS_COLOUR.test(pin) ? pin : undefined,
    zoomControls: zoomControls !== false,
    zoom: target,
    showLabels: showLabels === true,
    ...(zoomIntro === true
      ? { intro: caseMapIntro(target, zoomIntroFrom, zoomIntroDuration) }
      : {}),
  };
}

/**
 * The map's options besides its container and style: it opens on the case at `zoom`, or, with an `intro` that will
 * play, at the intro's level, which may be below the usual least zoom of 4.
 */
export function caseMapOptions({
  latitude,
  longitude,
  zoom,
  intro,
}: {
  latitude: number;
  longitude: number;
  zoom: number;
  intro?: CaseMapIntro;
}): Omit<MapOptions, "container" | "style"> {
  const start = intro?.from ?? zoom;
  return {
    center: [longitude, latitude],
    zoom: start,
    minZoom: Math.min(4, start),
    maxZoom: 18,
    cooperativeGestures: true,
    attributionControl: { compact: false },
    dragRotate: false,
    pitchWithRotate: false,
    touchPitch: false,
    locale: CASE_MAP_LOCALE,
  };
}

/**
 * The share of the map that must be in view before it glides in: half, or for a map more than about twice the screen's
 * height, which can never be half in view, as much as nine tenths of the screen holds.
 */
export function caseMapIntroThreshold(
  mapHeight: number,
  screenHeight: number,
): number {
  return mapHeight > 0 && screenHeight > 0
    ? Math.min(0.5, (0.9 * screenHeight) / mapHeight)
    : 0.5;
}

/** Ease-in-out cubic: the glide starts and lands softly. */
export const caseMapIntroEasing = (t: number) =>
  t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2;

const RGB =
  /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)\s*(?:[,/]\s*([\d.]+)%?\s*)?\)$/i;
const HEX = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
/** How a browser's computed style writes a colour-mix() result. */
const SRGB =
  /^color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*[\d.]+%?)?\)$/i;
const NAMED: Record<string, string> = { white: "#ffffff", black: "#000000" };

const channels = (hex: string) =>
  [1, 3, 5].map((at) => parseInt(hex.slice(at, at + 2), 16));
const toHex = (values: number[]) =>
  `#${values
    .map((value) =>
      Math.round(Math.max(0, Math.min(255, value)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;

/** A colour as `#rrggbb`, its alpha ignored: hex codes, rgb()/rgba(), color(srgb …) and white or black; undefined for
 *  anything else, a fully transparent rgba() among them. */
export function colourHex(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const text = value.trim().toLowerCase();
  if (NAMED[text]) return NAMED[text];
  const digits = HEX.exec(text)?.[1];
  if (digits) {
    const full =
      digits.length <= 4
        ? [...digits].map((digit) => digit + digit).join("")
        : digits;
    return `#${full.slice(0, 6)}`;
  }
  const rgb = RGB.exec(text);
  if (rgb)
    return rgb[4] !== undefined && Number(rgb[4]) === 0
      ? undefined
      : toHex([rgb[1], rgb[2], rgb[3]].map(Number));
  const srgb = SRGB.exec(text);
  return srgb
    ? toHex([srgb[1], srgb[2], srgb[3]].map((value) => Number(value) * 255))
    : undefined;
}

/** `share` of `to` in `from`, in sRGB as CSS's color-mix(in srgb) mixes. */
export function mixColours(from: string, to: string, share: number): string {
  const [a, b] = [channels(from), channels(to)];
  return toHex(a.map((value, index) => value + (b[index] - value) * share));
}

/** The least of `to` mixed into `from`, from `start` in steps of 2 %, that `ok` accepts; at most `most`. */
function toward(
  from: string,
  to: string,
  ok: (colour: string) => boolean,
  start: number,
  most = 0.6,
): string | undefined {
  for (let share = start; share <= most + 1e-9; share += 0.02) {
    const colour = mixColours(from, to, share);
    if (ok(colour)) return colour;
  }
  return undefined;
}

/** A colour in OKLCH: lightness 0–1, chroma, and hue in degrees. */
export interface Oklch {
  l: number;
  c: number;
  h: number;
}

const toLinear = (value: number) => {
  const channel = value / 255;
  return channel <= 0.04045
    ? channel / 12.92
    : ((channel + 0.055) / 1.055) ** 2.4;
};
const fromLinear = (value: number) =>
  255 *
  (value <= 0.0031308 ? 12.92 * value : 1.055 * value ** (1 / 2.4) - 0.055);

/** A `#rrggbb` colour in OKLCH. */
export function hexOklch(hex: string): Oklch {
  const [r, g, b] = channels(hex).map(toLinear);
  const [l, m, s] = [
    0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b,
    0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b,
    0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b,
  ].map(Math.cbrt);
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const yb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return {
    l: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    c: Math.hypot(a, yb),
    h: ((Math.atan2(yb, a) * 180) / Math.PI + 360) % 360,
  };
}

function linearRgb({ l, c, h }: Oklch): number[] {
  const a = c * Math.cos((h * Math.PI) / 180);
  const b = c * Math.sin((h * Math.PI) / 180);
  const [lc, mc, sc] = [
    l + 0.3963377774 * a + 0.2158037573 * b,
    l - 0.1055613458 * a - 0.0638541728 * b,
    l - 0.0894841775 * a - 1.291485548 * b,
  ].map((value) => value ** 3);
  return [
    4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc,
    -1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc,
    -0.0041960863 * lc - 0.7034186147 * mc + 1.707614701 * sc,
  ];
}

const inSrgb = (colour: Oklch) =>
  linearRgb(colour).every((value) => value >= -1e-6 && value <= 1 + 1e-6);

/** An OKLCH colour as `#rrggbb`, with as much of its chroma as sRGB can show at its lightness and hue. */
export function oklchHex(colour: Oklch): string {
  let chroma = colour.c;
  if (!inSrgb(colour)) {
    let [shown, lost] = [0, colour.c];
    for (let halving = 0; halving < 20; halving++) {
      const middle = (shown + lost) / 2;
      if (inSrgb({ ...colour, c: middle })) shown = middle;
      else lost = middle;
    }
    chroma = shown;
  }
  return toHex(linearRgb({ ...colour, c: chroma }).map(fromLinear));
}

/**
 * A natural colour's OKLCH hues, in degrees: what reads as it (`from`–`to`), the hue a website's own colours are
 * compared with (`natural`), and the natural colour drawn on a greyish (`muted`) and on a colourful (`vivid`) website.
 */
export interface NaturalHue {
  from: number;
  to: number;
  natural: number;
  muted: number;
  vivid: number;
}

/** Green areas read as green and water as blue: a forest green is about 142°, a lake about 230°. */
export const NATURAL_HUES: Record<"landuse" | "water", NaturalHue> = {
  landuse: { from: 120, to: 165, natural: 142, muted: 130, vivid: 145 },
  water: { from: 205, to: 255, natural: 230, muted: 240, vivid: 230 },
};

/** The OKLCH chroma of green areas and water: enough to read as a colour, not grey, and calm enough to stay a tint. */
export const NATURAL_CHROMA = { least: 0.025, most: 0.07 } as const;

/**
 * A website colour with less chroma than this is a grey, whatever its hue: a slate or midnight text or background
 * colour (0.02–0.03) is not the website's blue. A muted sage has a little more.
 */
const OWN_CHROMA = 0.035;

/** How many degrees nearer the natural hue a vivid website colour counts as, so a clear blue beats a blue-grey nearby. */
const VIVID_DEGREES = 12;

/** A website colour's hue is drawn this far inside the band, so a pale tint rounded to whole channels stays in it. */
const BAND_INSET = 3;

/** Whether `colour` reads as `hue`'s colour: its hue in the band and its chroma within NATURAL_CHROMA. */
export function readsAs(colour: string, hue: NaturalHue): boolean {
  const { c, h } = hexOklch(colour);
  return (
    h >= hue.from &&
    h <= hue.to &&
    c >= NATURAL_CHROMA.least &&
    c <= NATURAL_CHROMA.most
  );
}

/** How colourful a colour is, from 0 for a greyish one (chroma 0.03 or less) to 1 for a vivid one (0.15 or more). */
const vividness = (chroma: number) =>
  Math.min(1, Math.max(0, (chroma - 0.03) / 0.12));

/** A tint's chroma, never a saturated fill; less on a dark land, where the same chroma looks stronger. */
const TINT_CHROMA = { light: [0.03, 0.045], dark: [0.026, 0.036] } as const;

const tintChroma = (vivid: number, darkLand: boolean) => {
  const [muted, clear] = TINT_CHROMA[darkLand ? "dark" : "light"];
  return muted + (clear - muted) * vivid;
};

/**
 * The least step in OKLCH lightness away from the land, in steps of 0.005 (darker on a light land, lighter on a dark
 * one), at `h` and `c` that stands out from the land at `min` and reads as `hue`'s colour.
 */
function naturalTint(
  land: string,
  darkLand: boolean,
  { c, h }: Pick<Oklch, "c" | "h">,
  min: number,
  hue: NaturalHue,
): string | undefined {
  const { l } = hexOklch(land);
  for (let step = 0; step <= 120; step++) {
    const shift = step * 0.005;
    const colour = oklchHex({ l: darkLand ? l + shift : l - shift, c, h });
    if (contrast(colour, land) >= min && readsAs(colour, hue)) return colour;
  }
  return undefined;
}

/**
 * Green areas or water: a tint of the website's own colour nearest the natural hue, a vivid one counting as up to
 * VIVID_DEGREES nearer (the first listed on a tie), where one lies in the band with chroma; else of a natural colour as
 * muted or as vivid as the brand colour. The tint is as vivid as the colour it comes from.
 */
function naturalLayer(
  own: Oklch[],
  brand: Oklch,
  land: string,
  darkLand: boolean,
  min: number,
  hue: NaturalHue,
): string {
  const distance = ({ c, h }: Oklch) =>
    Math.abs(h - hue.natural) - VIVID_DEGREES * vividness(c);
  const inBand = own
    .filter(({ c, h }) => c >= OWN_CHROMA && h >= hue.from && h <= hue.to)
    .sort((a, b) => distance(a) - distance(b));
  for (const colour of inBand) {
    const h = Math.min(
      hue.to - BAND_INSET,
      Math.max(hue.from + BAND_INSET, colour.h),
    );
    const c = tintChroma(vividness(colour.c), darkLand);
    const tint = naturalTint(land, darkLand, { c, h }, min, hue);
    if (tint) return tint;
  }
  const vivid = vividness(brand.c);
  const c = tintChroma(vivid, darkLand);
  const h = hue.muted + (hue.vivid - hue.muted) * vivid;
  return (
    naturalTint(land, darkLand, { c, h }, min, hue) ??
    oklchHex({ l: hexOklch(land).l + (darkLand ? 0.1 : -0.1), c, h })
  );
}

/** The website's own colours the »Hjemmesidens farver« map is derived from, in one colour scheme. */
export interface SiteMapColours {
  page: string;
  text: string;
  brand: string;
  /** Lys baggrund on the look system, a surface colour otherwise. */
  soft?: string;
  /**
   * The website's other design colours in the scheme: all but the entries that give `page` and `text`, which are the
   * land and the ink. An entry with another role and the same colour stays. Its own green and blue are looked for among
   * these, the brand and the soft colour.
   */
  colours?: string[];
}

/**
 * The calm map of a website's colours: the land is the page (a shade darker where white roads would not show on it),
 * buildings and roads tints of the text; green areas are green and water blue, the website's own where it has one,
 * each the least tint that still stands out from the land.
 */
export function siteMapPalette(site: SiteMapColours): CaseMapPalette {
  const page = colourHex(site.page) ?? "#ffffff";
  const darkPage = (hexLuminance(page) ?? 1) < 0.18;
  const text =
    colourHex(site.text) && contrast(colourHex(site.text)!, page) >= 3
      ? colourHex(site.text)!
      : darkPage
        ? "#f2f2f2"
        : "#222222";
  const brand = colourHex(site.brand) ?? text;
  const own = [
    ...new Set(
      [site.brand, site.soft, ...(site.colours ?? [])]
        .map(colourHex)
        .filter((hex): hex is string => hex !== undefined),
    ),
  ].map(hexOklch);
  const land = darkPage
    ? page
    : (toward(page, text, (c) => contrast("#ffffff", c) >= 1.12, 0, 0.3) ??
      page);
  const apart = (min: number) => (colour: string) =>
    contrast(colour, land) >= min;
  const tint = (to: string, min: number, start: number) =>
    toward(land, to, apart(min), start);
  const natural = (min: number, hue: NaturalHue) =>
    naturalLayer(own, hexOklch(brand), land, darkPage, min, hue);
  const roads = darkPage
    ? (tint(text, 1.35, 0.12) ?? mixColours(land, text, 0.3))
    : (toward(land, "#ffffff", apart(1.1), 0.2, 0.9) ?? "#ffffff");
  return {
    land,
    landuse: natural(1.04, NATURAL_HUES.landuse),
    water: natural(1.2, NATURAL_HUES.water),
    buildings:
      tint(text, darkPage ? 1.15 : 1.1, darkPage ? 0.08 : 0.06) ??
      mixColours(land, text, 0.2),
    roads,
    ink: contrast(text, land) >= 4.5 ? text : darkPage ? "#f2f2f2" : "#1f2328",
  };
}

/** `candidate` where it stands out from the land at 3:1, else the palette's ink. */
export function readablePin(
  candidate: string | undefined,
  palette: CaseMapPalette,
): string {
  const hex = colourHex(candidate);
  return hex && contrast(hex, palette.land) >= PIN_CONTRAST ? hex : palette.ink;
}

/**
 * The pin's colour in `scheme`, with `resolve` giving what the page draws a CSS colour as: the stored Nålens farve exactly
 * as resolved; where none is stored or it resolves to no colour, the brand colour, given way to the ink where it would
 * not stand out from the land.
 */
export function caseMapPinColour(
  scheme: CaseMapScheme,
  resolve: (value: string) => string | undefined,
): string {
  const chosen = scheme.pin === undefined ? undefined : resolve(scheme.pin);
  return chosen ?? readablePin(resolve(scheme.brand), scheme.palette);
}

type Schemes<T> = Record<LookScheme, T>;

function lookSiteColours(theme: ThemeWithLookSystem): Schemes<SiteMapColours> {
  const system = record(theme.colorSystem);
  const derived = record(system.derived);
  const palette = Array.isArray(system.palette) ? system.palette : [];
  const v2 = isDerivedV2(theme);
  const roleKey = (name: keyof typeof ROLE_PALETTE_KEYS) => {
    const named = record(derived.roles)[name];
    return (
      (typeof named === "string"
        ? resolvePaletteKey(system, named)
        : undefined) ?? resolvePaletteKey(system, ROLE_PALETTE_KEYS[name])
    );
  };
  const role = (name: keyof typeof ROLE_PALETTE_KEYS, scheme: LookScheme) => {
    const key = roleKey(name);
    const entry = palette.find((item) => record(item).key === key);
    return entry
      ? paletteSchemes(system, entry, v2)?.[scheme].colour
      : undefined;
  };
  const landAndInk = new Set([roleKey("background"), roleKey("text")]);
  const scheme = (name: LookScheme): SiteMapColours => {
    const page = record(record(derived[name]).page);
    const fallback =
      name === "dark" ? ["#1d1f22", "#f2f2f2"] : ["#ffffff", "#222222"];
    return {
      page: isColour(page.bg)
        ? page.bg
        : (role("background", name) ?? fallback[0]),
      text: isColour(page.text)
        ? page.text
        : (role("text", name) ?? fallback[1]),
      brand: role("brand", name) ?? role("accent", name) ?? fallback[1],
      soft: role("soft", name),
      colours: palette.flatMap((entry) => {
        const key = record(entry).key;
        if (typeof key === "string" && landAndInk.has(key)) return [];
        const held = paletteSchemes(system, entry, v2)?.[name].colour;
        return held ? [held] : [];
      }),
    };
  };
  return { light: scheme("light"), dark: scheme("dark") };
}

/** Udseende's palette keys besides the page's `background` and `text`, in the order the map looks through them. */
const APPEARANCE_COLOURS = [
  "muted",
  "surface",
  "border",
  "accent",
  "accentText",
] as const;
/** Theme keys that hold no classic design colours: Udseende's palettes are read on their own, the rest are not colours. */
const NOT_CLASSIC_COLOURS = new Set([
  "appearance",
  "typography",
  "colorSystem",
]);

/**
 * A classic theme's colours with their paths, in the order it stores them: every `…Color` and `color`, its error
 * colours left out.
 */
function classicColours(theme: unknown, at = ""): [string, string][] {
  return Object.entries(record(theme)).flatMap(
    ([key, value]): [string, string][] => {
      const path = at ? `${at}.${key}` : key;
      if (NOT_CLASSIC_COLOURS.has(key) || /error/i.test(key)) return [];
      if (typeof value !== "string") return classicColours(value, path);
      return /(?:^c|C)olor$/.test(key) ? [[path, value]] : [];
    },
  );
}

/** The website's colours per scheme: the look system's palette and pages, Udseende's palettes, or the classic theme. */
export function siteMapColours(theme: ThemeData): Schemes<SiteMapColours> {
  if (isLookSystem(theme)) return lookSiteColours(theme);
  const palettes = theme.appearance?.palettes;
  const landAndInk = new Set([
    "page.bgColor",
    theme.page?.textColor ? "page.textColor" : "base.textColor",
  ]);
  const classic: SiteMapColours = {
    page: theme.page?.bgColor || "#ecf0f1",
    text: theme.page?.textColor || theme.base?.textColor || "#4b5563",
    brand: theme.button?.bgColor || "#3C3C3B",
    colours: classicColours(theme).flatMap(([path, colour]) =>
      landAndInk.has(path) ? [] : [colour],
    ),
  };
  const fromPalette = (
    palette: NonNullable<typeof palettes>["light"],
  ): SiteMapColours | undefined =>
    palette && {
      page: palette.background,
      text: palette.text,
      brand: palette.accent,
      soft: palette.surface,
      colours: APPEARANCE_COLOURS.flatMap((key) =>
        typeof palette[key] === "string" ? [palette[key]] : [],
      ),
    };
  const light = fromPalette(palettes?.light) ?? classic;
  return { light, dark: fromPalette(palettes?.dark) ?? light };
}

/**
 * Everything the map draws, per colour scheme. A fixed theme is the same in both. An own colour code as the pin takes the
 * dark version `derived.darkOwn` names only where the land changes in dark, as a mark on an own colour's fill does.
 */
export function caseMapDeluxeView(
  theme: ThemeData,
  specifics: unknown,
): CaseMapDeluxeView {
  const { pinColor, ...settings } = caseMapDeluxeSettings(specifics);
  const site = siteMapColours(theme);
  const palette = (name: LookScheme) =>
    settings.theme === "site"
      ? siteMapPalette(site[name])
      : CASE_MAP_THEMES[settings.theme];
  const palettes = { light: palette("light"), dark: palette("dark") };
  const pin = (name: LookScheme) =>
    pinColor !== undefined &&
    name === "dark" &&
    palettes.light.land !== palettes.dark.land
      ? (ownColourInDark(theme, pinColor) ?? pinColor)
      : pinColor;
  const scheme = (name: LookScheme): CaseMapScheme => {
    const chosen = pin(name);
    return {
      palette: palettes[name],
      ...(chosen === undefined ? {} : { pin: chosen }),
      brand: site[name].brand,
    };
  };
  return { ...settings, light: scheme("light"), dark: scheme("dark") };
}

/** Greenland's rough bounding box, as the REdesign map and the render service draw it. */
export function mapTilesRegion(
  latitude: number,
  longitude: number,
): MapTilesRegion {
  return latitude >= 59 &&
    latitude <= 84 &&
    longitude >= -75 &&
    longitude <= -10
    ? "gl"
    : "dk";
}

export const MAP_ATTRIBUTION =
  '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">© OpenStreetMap contributors</a>';

/** Green landuse; the rest (housing, shops, schools, fields …) is drawn as land, so towns and the countryside stay calm. */
const GREEN_LANDUSE = [
  "park",
  "forest",
  "wood",
  "garden",
  "grass",
  "grassland",
  "meadow",
  "scrub",
  "playground",
  "pitch",
  "golf_course",
  "cemetery",
  "dog_park",
  "allotments",
  "recreation_ground",
  "village_green",
  "nature_reserve",
  "national_park",
  "orchard",
  "vineyard",
];

/** What a name must reach against its halo (WCAG text contrast). */
export const NAME_CONTRAST = 4.5;

/** The names' colours, each read on a halo of one of the map's own colours. */
export interface CaseMapLabelPalette {
  /** Towns and villages, on the land. */
  places: string;
  /** Neighbourhoods, landmarks and countries: quieter, on the land. */
  quiet: string;
  /** Street names, on the road colour, so the road seems to widen under the name. */
  streets: string;
  /** Water names, a shade of the water, on the water. */
  water: string;
}

/** `colour`, or the least of black or white (whichever reads better on `on`) mixed into it that reads on `on`. */
function readableOn(colour: string, on: string): string {
  if (contrast(colour, on) >= NAME_CONTRAST) return colour;
  const end =
    contrast("#000000", on) >= contrast("#ffffff", on) ? "#000000" : "#ffffff";
  return (
    toward(colour, end, (c) => contrast(c, on) >= NAME_CONTRAST, 0.02, 1) ?? end
  );
}

/** The most of `on` mixed into `from`, at most `most`, that still reads on `on`. */
function quietest(from: string, on: string, most: number): string {
  for (let share = most; share > 1e-9; share -= 0.02) {
    const colour = mixColours(from, on, share);
    if (contrast(colour, on) >= NAME_CONTRAST) return colour;
  }
  return readableOn(from, on);
}

/** The names' colours of a map palette: its ink, quieter tints of it, and a deep shade of the water. */
export function caseMapLabelPalette(
  palette: CaseMapPalette,
): CaseMapLabelPalette {
  const places = readableOn(palette.ink, palette.land);
  return {
    places,
    quiet: quietest(places, palette.land, 0.3),
    streets: quietest(readableOn(places, palette.roads), palette.roads, 0.25),
    water:
      toward(
        palette.water,
        places,
        (colour) => contrast(colour, palette.water) >= NAME_CONTRAST,
        0.4,
        1,
      ) ?? readableOn(places, palette.water),
  };
}

/** The Danish name where the basemap has one, else the local name. */
const NAME: ExpressionSpecification = [
  "coalesce",
  ["get", "name:da"],
  ["get", "name"],
];
const HALO_WIDTH = 1.5;
const STREETS = ["highway", "major_road", "minor_road", "other"];
/** The basemap counts a pedestrian street (gågade) as a path, but it has addresses; footways and cycleways are not named. */
const PEDESTRIAN_STREET: ExpressionSpecification = [
  "all",
  ["==", ["get", "kind"], "path"],
  ["==", ["get", "kind_detail"], "pedestrian"],
];
/** Settlements only: the basemap's other localities are bastions, shoals, squares and single farms. */
const SETTLEMENTS = ["city", "town", "village", "hamlet"];
/** A name waits for the zoom the basemap gives it, as its tile may carry it a zoom early. */
const FROM_OWN_ZOOM: ExpressionSpecification = [
  ">=",
  ["zoom"],
  ["to-number", ["get", "min_zoom"], 0],
];
/** Landmarks a buyer looks for; shops, restaurants, hotels and the like are left out, so the map stays calm. */
const LANDMARKS = [
  "station",
  "school",
  "kindergarten",
  "childcare",
  "university",
  "college",
  "hospital",
  "library",
  "museum",
  "townhall",
  "place_of_worship",
  "park",
  "nature_reserve",
  "forest",
  "beach",
  "marina",
  "stadium",
  "sports_centre",
  "zoo",
];
const RANK: ExpressionSpecification = [
  "to-number",
  ["get", "population_rank"],
  0,
];

interface NameLayer {
  id: string;
  sourceLayer: string;
  colour: keyof CaseMapLabelPalette;
  halo: keyof CaseMapPalette;
  filter: FilterSpecification;
  layout: NonNullable<SymbolLayerSpecification["layout"]>;
  minzoom?: number;
  maxzoom?: number;
}

/**
 * The names, lowest priority first: MapLibre places an upper layer's names first, so a town's name wins over a
 * street's. Rivers and streams are drawn as lines without names, so only water with a label point is named.
 */
const NAME_LAYERS: NameLayer[] = [
  {
    id: "label-water",
    sourceLayer: "water",
    colour: "water",
    halo: "water",
    filter: ["all", ["==", ["geometry-type"], "Point"], ["has", "name"]],
    layout: {
      "text-field": NAME,
      "text-font": ["NotoSans-Italic"],
      "text-size": ["interpolate", ["linear"], ["zoom"], 10, 11, 15, 13],
      "text-letter-spacing": 0.05,
      "text-max-width": 8,
    },
  },
  {
    id: "label-streets",
    sourceLayer: "roads",
    colour: "streets",
    halo: "roads",
    minzoom: 13,
    filter: [
      "all",
      ["any", ["in", ["get", "kind"], ["literal", STREETS]], PEDESTRIAN_STREET],
      ["has", "name"],
    ],
    layout: {
      "symbol-placement": "line",
      "symbol-sort-key": ["to-number", ["get", "min_zoom"], 99],
      "text-field": NAME,
      "text-font": ["NotoSans-Regular"],
      "text-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        13,
        10.5,
        16,
        12,
        18,
        13.5,
      ],
    },
  },
  {
    id: "label-landmarks",
    sourceLayer: "pois",
    colour: "quiet",
    halo: "land",
    minzoom: 15,
    filter: [
      "all",
      ["in", ["get", "kind"], ["literal", LANDMARKS]],
      ["has", "name"],
      FROM_OWN_ZOOM,
    ],
    layout: {
      "symbol-sort-key": ["to-number", ["get", "min_zoom"], 99],
      "text-field": NAME,
      "text-font": ["NotoSans-Regular"],
      "text-size": 11.5,
      "text-max-width": 8,
      "text-padding": 6,
    },
  },
  {
    id: "label-neighbourhoods",
    sourceLayer: "places",
    colour: "quiet",
    halo: "land",
    filter: [
      "all",
      ["in", ["get", "kind"], ["literal", ["neighbourhood", "macrohood"]]],
      FROM_OWN_ZOOM,
    ],
    layout: {
      "symbol-sort-key": ["to-number", ["get", "min_zoom"], 99],
      "text-field": NAME,
      "text-font": ["NotoSans-Regular"],
      "text-size": ["interpolate", ["linear"], ["zoom"], 12, 10.5, 16, 12.5],
      "text-transform": "uppercase",
      "text-letter-spacing": 0.08,
      "text-max-width": 8,
      "text-padding": 6,
    },
  },
  {
    id: "label-places",
    sourceLayer: "places",
    colour: "places",
    halo: "land",
    filter: [
      "all",
      ["==", ["get", "kind"], "locality"],
      ["in", ["get", "kind_detail"], ["literal", SETTLEMENTS]],
      FROM_OWN_ZOOM,
    ],
    layout: {
      "symbol-sort-key": [
        "to-number",
        ["get", "sort_key"],
        ["get", "min_zoom"],
        99,
      ],
      "text-field": NAME,
      "text-font": ["NotoSans-Medium"],
      "text-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        6,
        ["step", RANK, 10, 9, 12, 11, 14],
        10,
        ["step", RANK, 11, 7, 13, 9, 15, 11, 18],
        14,
        ["step", RANK, 13, 7, 15, 9, 17, 11, 20],
        18,
        ["step", RANK, 14, 7, 16, 9, 19, 11, 22],
      ],
      "text-max-width": 8,
      "text-padding": 8,
    },
  },
  {
    id: "label-countries",
    sourceLayer: "places",
    colour: "quiet",
    halo: "land",
    maxzoom: 7,
    filter: ["==", ["get", "kind"], "country"],
    layout: {
      "text-field": NAME,
      "text-font": ["NotoSans-Medium"],
      "text-size": ["interpolate", ["linear"], ["zoom"], 4, 11, 7, 14],
      "text-transform": "uppercase",
      "text-letter-spacing": 0.1,
      "text-max-width": 8,
    },
  },
];

function nameLayers(palette: CaseMapPalette): SymbolLayerSpecification[] {
  const names = caseMapLabelPalette(palette);
  return NAME_LAYERS.map(
    ({ id, sourceLayer, colour, halo, filter, layout, minzoom, maxzoom }) => ({
      id,
      type: "symbol",
      source: "protomaps",
      "source-layer": sourceLayer,
      ...(minzoom === undefined ? {} : { minzoom }),
      ...(maxzoom === undefined ? {} : { maxzoom }),
      filter,
      layout,
      paint: {
        "text-color": names[colour],
        "text-halo-color": palette[halo],
        "text-halo-width": HALO_WIDTH,
      },
    }),
  );
}

/** Protomaps' water layer also holds rivers and streams as lines; a fill would close each line into a lake. */
const AREAS: ExpressionSpecification = [
  "match",
  ["geometry-type"],
  ["Polygon", "MultiPolygon"],
  true,
  false,
];

const LINES: ExpressionSpecification = [
  "match",
  ["geometry-type"],
  ["LineString", "MultiLineString"],
  true,
  false,
];

/** Waterways drawn as lines; ditches and drains are left out, as on most maps. */
const WATERWAYS = ["river", "canal", "stream"];

/** Streets and paths by rank; railways get their own thin line, and ferry routes over the sea are not drawn. */
const DRAWN_STREETS = ["highway", "major_road", "minor_road", "other", "path"];

/** Each street rank's width at a zoom: paths appear from 14, so the countryside is not a web of tracks. */
const streetWidth = (
  highway: number,
  major: number,
  minor: number,
  path: number,
): ExpressionSpecification => [
  "match",
  ["get", "kind"],
  "highway",
  highway,
  "major_road",
  major,
  "path",
  path,
  minor,
];

/**
 * The REdesign map's style: our Protomaps basemap through the pmtiles protocol, served by the website itself at
 * /map-tiles. Without names it needs no glyphs or sprite and fetches nothing else; with them (Vis navne) the glyphs come
 * from the website's own /map-glyphs.
 */
export function caseMapStyle(
  region: MapTilesRegion,
  palette: CaseMapPalette,
  { labels = false }: { labels?: boolean } = {},
): StyleSpecification {
  const layers: LayerSpecification[] = [
    {
      id: "background",
      type: "background",
      paint: { "background-color": palette.land },
    },
    {
      id: "earth",
      type: "fill",
      source: "protomaps",
      "source-layer": "earth",
      filter: AREAS,
      paint: { "fill-color": palette.land },
    },
    {
      id: "landuse",
      type: "fill",
      source: "protomaps",
      "source-layer": "landuse",
      filter: [
        "all",
        AREAS,
        ["in", ["get", "kind"], ["literal", GREEN_LANDUSE]],
      ],
      paint: { "fill-color": palette.landuse },
    },
    {
      id: "water",
      type: "fill",
      source: "protomaps",
      "source-layer": "water",
      filter: AREAS,
      paint: { "fill-color": palette.water },
    },
    {
      id: "waterways",
      type: "line",
      source: "protomaps",
      "source-layer": "water",
      filter: ["all", LINES, ["in", ["get", "kind"], ["literal", WATERWAYS]]],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": palette.water,
        "line-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          10,
          ["match", ["get", "kind"], "stream", 0.4, 0.8],
          14,
          ["match", ["get", "kind"], "stream", 1.2, 2],
          18,
          ["match", ["get", "kind"], "stream", 2.5, 5],
        ],
      },
    },
    {
      id: "buildings",
      type: "fill",
      source: "protomaps",
      "source-layer": "buildings",
      filter: AREAS,
      minzoom: 13,
      paint: { "fill-color": palette.buildings },
    },
    {
      id: "railways",
      type: "line",
      source: "protomaps",
      "source-layer": "roads",
      minzoom: 10,
      filter: ["all", LINES, ["==", ["get", "kind"], "rail"]],
      paint: {
        "line-color": palette.ink,
        "line-opacity": 0.3,
        "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.5, 18, 2],
      },
    },
    {
      id: "roads",
      type: "line",
      source: "protomaps",
      "source-layer": "roads",
      filter: [
        "all",
        LINES,
        ["in", ["get", "kind"], ["literal", DRAWN_STREETS]],
      ],
      layout: {
        "line-cap": "round",
        "line-join": "round",
        "line-sort-key": ["coalesce", ["get", "sort_rank"], 0],
      },
      paint: {
        "line-color": palette.roads,
        "line-opacity": [
          "case",
          ["boolean", ["get", "is_tunnel"], false],
          0.4,
          1,
        ],
        "line-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          6,
          streetWidth(0.8, 0.5, 0, 0),
          12,
          streetWidth(2, 1.5, 0.6, 0),
          13,
          streetWidth(2.5, 2, 1, 0),
          14,
          streetWidth(3, 2.5, 1.5, 0.6),
          18,
          streetWidth(9, 8, 6, 2),
        ],
      },
    },
  ];
  return {
    version: 8,
    ...(labels ? { glyphs: MAP_GLYPHS_URL } : {}),
    sources: {
      protomaps: {
        type: "vector",
        url: `pmtiles:///map-tiles/${region}.pmtiles`,
        attribution: MAP_ATTRIBUTION,
      },
    },
    layers: labels ? [...layers, ...nameLayers(palette)] : layers,
  };
}

export type CaseMapPaint = [layer: string, property: string, colour: string];

/** Each layer's paint property that carries a palette colour, with that colour: what a scheme change repaints. */
export function caseMapPaint(
  palette: CaseMapPalette,
  labels = false,
): CaseMapPaint[] {
  const paint: CaseMapPaint[] = [
    ["background", "background-color", palette.land],
    ["earth", "fill-color", palette.land],
    ["landuse", "fill-color", palette.landuse],
    ["water", "fill-color", palette.water],
    ["waterways", "line-color", palette.water],
    ["buildings", "fill-color", palette.buildings],
    ["railways", "line-color", palette.ink],
    ["roads", "line-color", palette.roads],
  ];
  if (!labels) return paint;
  const names = caseMapLabelPalette(palette);
  for (const { id, colour, halo } of NAME_LAYERS)
    paint.push(
      [id, "text-color", names[colour]],
      [id, "text-halo-color", palette[halo]],
    );
  return paint;
}

/** MapLibre's texts in Danish: the gesture hints, the zoom buttons and the map's and pin's names. */
export const CASE_MAP_LOCALE: Record<string, string> = {
  "CooperativeGesturesHandler.WindowsHelpText": "Zoom med Ctrl + scroll",
  "CooperativeGesturesHandler.MacHelpText": "Zoom med ⌘ + scroll",
  "CooperativeGesturesHandler.MobileHelpText": "Flyt kortet med to fingre",
  "NavigationControl.ZoomIn": "Zoom ind",
  "NavigationControl.ZoomOut": "Zoom ud",
  "AttributionControl.ToggleAttribution": "Vis eller skjul kilder",
  "Map.Title": "Kort over ejendommens placering",
  "Marker.Title": "Ejendommens placering",
};
